type ScrollTarget = Window | HTMLElement;

type CancelableSmoothScrollOptions = {
  top: number;
  left?: number;
  durationMs?: number;
};

const INTERRUPTION_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
]);

function isWindowTarget(target: ScrollTarget): target is Window {
  return "scrollY" in target;
}

function getCurrentPosition(target: ScrollTarget) {
  if (isWindowTarget(target)) {
    return { top: target.scrollY, left: target.scrollX };
  }

  return { top: target.scrollTop, left: target.scrollLeft };
}

function scrollImmediately(target: ScrollTarget, top: number, left: number) {
  if (isWindowTarget(target)) {
    target.scrollTo({ top, left, behavior: "auto" });
    return;
  }

  target.scrollTo({ top, left, behavior: "auto" });
}

function isRelevantKey(event: KeyboardEvent) {
  if (event.altKey || event.ctrlKey || event.metaKey) {
    return false;
  }

  return INTERRUPTION_KEYS.has(event.key);
}

export function startCancelableSmoothScroll(
  target: ScrollTarget,
  { top, left = 0, durationMs = 700 }: CancelableSmoothScrollOptions,
) {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  let isCancelled = false;

  const cleanupCallbacks: Array<() => void> = [];

  const removeListeners = () => {
    while (cleanupCallbacks.length > 0) {
      cleanupCallbacks.pop()?.();
    }
  };

  const cancel = () => {
    if (isCancelled) return;
    isCancelled = true;

    window.clearTimeout(timeoutId);
    const currentPosition = getCurrentPosition(target);
    scrollImmediately(target, currentPosition.top, currentPosition.left);
    removeListeners();
  };

  const finish = () => {
    if (isCancelled) return;
    isCancelled = true;
    removeListeners();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (isRelevantKey(event)) {
      cancel();
    }
  };

  const addListener = <K extends keyof WindowEventMap>(
    listenerTarget: Window | HTMLElement | Document,
    eventName: K,
    listener: (event: WindowEventMap[K]) => void,
  ) => {
    listenerTarget.addEventListener(eventName, listener as EventListener, {
      passive: true,
      capture: true,
    });

    cleanupCallbacks.push(() => {
      listenerTarget.removeEventListener(eventName, listener as EventListener, {
        capture: true,
      });
    });
  };

  addListener(window, "wheel", cancel);
  addListener(window, "touchmove", cancel);
  addListener(window, "keydown", onKeyDown);

  if (!isWindowTarget(target)) {
    addListener(target, "wheel", cancel);
    addListener(target, "touchmove", cancel);
  }

  if (isWindowTarget(target)) {
    target.scrollTo({ top, left, behavior: "smooth" });
  } else {
    target.scrollTo({ top, left, behavior: "smooth" });
  }

  const timeoutId = window.setTimeout(finish, durationMs);

  cleanupCallbacks.push(() => {
    window.clearTimeout(timeoutId);
  });

  return cancel;
}
